import type React from 'react';
import type { BreadcrumbItem } from '../ui/Breadcrumbs.js';
import { PageHeader, type PageHelpContent } from '../ui/PageHeader.js';
import { type TabItem, Tabs } from '../ui/Tabs.js';
import { useInEntityPanel } from './entity-panel-scope.js';

interface DetailPageProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  /** Status badge(s) beside the title. */
  badges?: React.ReactNode;
  helpContent?: PageHelpContent;
  /** The primary action, then a `Menu` of the rest (Edit, Duplicate, Archive…). */
  actions?: React.ReactNode;
  /** A row under the title: owner, updated time, counts. */
  meta?: React.ReactNode;
  /** Shown above the content (a failed load, a pending approval, a "this is a draft" note). */
  alert?: React.ReactNode;
  /** Section tabs; keep the active tab in the URL with `useTabParam`. */
  tabs?: readonly TabItem[];
  activeTab?: string;
  onTabChange?: (id: string) => void;
  /** The right rail: facts, used-by, recent activity. Stacks under the content below `xl`. */
  rail?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * The detail page template: title with status and actions, URL-synced section tabs, then the content with an optional
 * sticky right rail.
 */
export function DetailPage({
  title,
  subtitle,
  breadcrumbs,
  badges,
  helpContent,
  actions,
  meta,
  alert,
  tabs,
  activeTab,
  onTabChange,
  rail,
  children,
}: DetailPageProps) {
  // In a side panel the window's width says nothing about the panel's: the rail always stacks under the content.
  const inPanel = useInEntityPanel();
  return (
    <div className="space-y-5">
      <PageHeader
        title={title}
        subtitle={subtitle}
        breadcrumbs={breadcrumbs}
        badges={badges}
        helpContent={helpContent}
        actions={actions}
        meta={meta}
      />
      {alert}
      {tabs && tabs.length > 0 && activeTab !== undefined && onTabChange && (
        <Tabs tabs={tabs} activeTab={activeTab} onChange={onTabChange} aria-label={`${title} sections`} />
      )}
      {rail ? (
        <div
          className={
            inPanel
              ? 'grid grid-cols-1 items-start gap-5'
              : 'grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_340px]'
          }
        >
          <div className="min-w-0 space-y-5">{children}</div>
          <aside className={inPanel ? 'flex flex-col gap-5' : 'flex flex-col gap-5 xl:sticky xl:top-0'}>{rail}</aside>
        </div>
      ) : (
        <div className="min-w-0 space-y-5">{children}</div>
      )}
    </div>
  );
}
